import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";
import { useDispatch, useSelector } from "react-redux";

import { sessionDataHandler } from "../sessionDataHandler";
import { descartarOrigemDaReconexao, marcarOrigemDaReconexao } from "../store/actions/listeners/reconnect";
import { ACTION_RESET_CONVERSATION } from "../store/actions/actionTypes";
import { changeEngagementPhase, updatePreEngagementData, updatePreEngagementStep } from "../store/actions/genericActions";
import { AppState, EngagementPhase } from "../store/definitions";
import { deveMostrarCsatNaDespedida } from "../store/selectors";
import { containerStyles, textStyles, titleStyles } from "./styles/ConversationEnded.styles";
import { CsatCard } from "./CsatCard";

export const ConversationEnded = () => {
    const dispatch = useDispatch();
    /*
     * Seletor MÍNIMO de propósito: o transcript foi removido, e com ele `messages`, `users`,
     * `preEngagementData` e `transcriptConfig`. Só o client sobra, porque o encerramento precisa
     * desligá-lo. Reintroduzir os outros aqui ressuscitaria dependência de algo que não existe mais.
     */
    const conversationsClient = useSelector((state: AppState) => state.chat.conversationsClient);
    /*
     * Quem já avaliou DURANTE o atendimento não vê nada aqui — nem a escala, nem o agradecimento. O
     * agradecimento é retorno de uma ação que a pessoa acabou de fazer; na despedida ela não fez nada.
     * Quem ignorou a pesquisa no meio continua sendo perguntado. Regra do Mateus, 2026-09-12.
     */
    const mostrarCsat = useSelector((state: AppState) => deveMostrarCsatNaDespedida(state.chat));

    const handleStartNewChat = () => {
        sessionDataHandler.clear();
        /*
         * Marca a origem ANTES de desligar: o `shutdown()` leva a conexão a `disconnected`, o
         * listener de `connectionStateChanged` dispara a reconexão e ela falha — alerta que NÓS
         * provocamos. Sem a marca, ele chega indistinguível de apostador com problema de rede.
         * Só rotula; não muda comportamento nenhum.
         */
        if (conversationsClient) {
            marcarOrigemDaReconexao(conversationsClient, "desligamento_proprio");
        }
        // Desliga o client da conversa anterior best-effort antes de trocar de fase (nunca lança e nunca bloqueia).
        try {
            /*
             * Se o desligamento FALHAR, a marca tem de sair: o client segue vivo e uma queda real
             * futura dele sairia rotulada como nossa. Falso negativo é melhor que causa falsa.
             */
            void conversationsClient?.shutdown?.().catch(() => {
                if (conversationsClient) {
                    descartarOrigemDaReconexao(conversationsClient);
                }
            });
        } catch {
            // Best-effort: nunca lança nem bloqueia o início da nova conversa
            if (conversationsClient) {
                descartarOrigemDaReconexao(conversationsClient);
            }
        }
        // Limpa os campos default do form (keys de contrato) ao iniciar nova conversa.
        dispatch(updatePreEngagementData({ friendlyName: "", cpf: "" }));
        dispatch(updatePreEngagementStep(1));
        // Solta a conversa encerrada: deixá-la no estado é o que permitiria enviar pra ela (→ 400).
        dispatch({ type: ACTION_RESET_CONVERSATION, payload: {} });
        dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
    };

    return (
        <Box {...containerStyles}>
            <Text as="h3" {...titleStyles}>
                Obrigado por conversar com a gente!
            </Text>
            {/* CSAT (v2): renderiza só quando há pesquisa pendente + marca com CSAT ligado; senão, nada. */}
            {mostrarCsat && <CsatCard />}
            <Text as="p" {...textStyles}>
                Se tiver mais dúvidas, fale com a gente novamente.
            </Text>
            <Button variant="primary" data-test="start-new-chat-button" onClick={handleStartNewChat}>
                Iniciar nova conversa
            </Button>
        </Box>
    );
};
